import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import Heartbeats from '../features/Heartbeats';
import RankingFluctuationAfterHours from '../features/RTRankingFluctuationAfterHours';
import BacktestingChart from '../features/BTRankingFluctuationDaily';
import AfterHoursBacktestingChart from '../features/BTRankingFluctuationAfterHours';
import BTCoinBacktest from '../features/BTCoinBacktest';
import BTCoinBacktestMock from '../features/BTCoinBacktestMock';
import RealtimeRankingFluctuation from '../features/RTRankingFluctuationDaily';
import RTUpbitChart from '../features/RTUpbitChart';
import RTUpbitChartBTC from '../features/RTUpbitChartBTC';
import RTUpbitChartETH from '../features/RTUpbitChartETH';
import RTUpbitChartSOL from '../features/RTUpbitChartSOL';
import RTUpbitChartAAVE from '../features/RTUpbitChartAAVE';
import RTUpbitChartXRP from '../features/RTUpbitChartXRP';
import RTUpbitChartLINK from '../features/RTUpbitChartLINK';
import RTUpbitChartENS from '../features/RTUpbitChartENS';

const Dashboard: React.FC = () => {
  // 환경 변수 확인 (프로덕션에서는 백테스트 섹션 숨김)
  const isProduction = process.env.REACT_APP_ENV === 'mercury-frontend-prod';
  
  // 급상승탐지 ON/OFF 상태
  const [surgeDetectionEnabled, setSurgeDetectionEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // 페이지 로드 시 Redis 상태 확인
  useEffect(() => {
    const checkRedisStatus = async () => {
      try {
        const response = await fetch(`/mercury-backend/settings/surge-detection?action=status`);
        if (response.ok) {
          const data = await response.json();
          const isEnabled = data.data?.is_enabled || false;
          setSurgeDetectionEnabled(isEnabled);
          console.log('🔄 Redis 상태 동기화:', isEnabled ? 'ON' : 'OFF');
        }
      } catch (error) {
        console.error('❌ Redis 상태 확인 오류:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    checkRedisStatus();
  }, []);

  // 급상승탐지 설정만 관리 (초기화 완료 후에만 실행)
  useEffect(() => {
    if (!isInitialized) return; // 초기화가 완료되지 않았으면 실행하지 않음

    if (surgeDetectionEnabled) {
      // 설정 활성화
      (async () => {
        try {
          const response = await fetch(`/mercury-backend/settings/surge-detection?action=enable`);
          if (response.ok) {
            console.log('✅ 급상승탐지 전략 활성화 완료');
          }
        } catch (error) {
          console.error('❌ 급상승탐지 전략 활성화 오류:', error);
        }
      })();
    } else {
      // 설정 비활성화
      (async () => {
        try {
          const response = await fetch(`/mercury-backend/settings/surge-detection?action=disable`);
          if (response.ok) {
            console.log('✅ 급상승탐지 전략 비활성화 완료');
          }
        } catch (error) {
          console.error('❌ 급상승탐지 전략 비활성화 오류:', error);
        }
      })();
    }
  }, [surgeDetectionEnabled, isInitialized]);
  return (
    <div className="dashboard-page">
      <div className="dashboard-sections">

        {/* 등락률 백테스트 섹션 - 가장 위 */}
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>등락률 백테스트</h2>
          </div>
          <div className="section-content" style={{ width: '100%', maxWidth: 1500, margin: '0 auto', alignItems: 'stretch' }}>
            {isProduction ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 프로덕션 환경에서는 이용할 수 없습니다
                </p>
              </div>
            ) : (
              <BacktestingChart />
            )}
          </div>
        </div>

        {/* 실시간 등락률 | 실시간 시간외 등락률 | 시간외 등락률 백테스트 - 나란히 */}
        <div className="dashboard-section-row">
          <div className="dashboard-section">
            <div className="section-header">
              <h2>실시간 등락률</h2>
            </div>
            <div className="section-content">
              <RealtimeRankingFluctuation />
              <div style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                padding: '16px',
                fontSize: '12px',
                color: '#e6edf3',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ color: '#fbbf24' }}>급상승탐지</div>
                <div
                  onClick={() => setSurgeDetectionEnabled(!surgeDetectionEnabled)}
                  style={{
                    width: '50px',
                    height: '24px',
                    background: surgeDetectionEnabled ? '#10b981' : '#6b7280',
                    borderRadius: '12px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: surgeDetectionEnabled ? '28px' : '2px',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-section">
            <div className="section-header">
              <h2>실시간 시간외 등락률</h2>
            </div>
            <div className="section-content">
              {!isProduction ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  color: '#6c757d'
                }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    🚫 로컬 환경에서는 이용할 수 없습니다
                  </p>
                </div>
              ) : (
                <RankingFluctuationAfterHours />
              )}
            </div>
          </div>
          <div className="dashboard-section">
            <div className="section-header">
              <h2>시간외 등락률 백테스트</h2>
            </div>
            <div className="section-content">
              {isProduction ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  color: '#6c757d'
                }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    🚫 프로덕션 환경에서는 이용할 수 없습니다
                  </p>
                </div>
              ) : (
                <AfterHoursBacktestingChart />
              )}
            </div>
          </div>
        </div>

        {/* 실시간 Upbit 차트 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트</h2>
          </div>
          <div className="section-content">
            <RTUpbitChart />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-BTC) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-BTC)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartBTC />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-ETH) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-ETH)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartETH />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-SOL) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-SOL)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartSOL />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-AAVE) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-AAVE)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartAAVE />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-XRP) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-XRP)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartXRP />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-LINK) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-LINK)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartLINK />
          </div>
        </div>

        {/* 실시간 Upbit 차트 (KRW-ENS) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>실시간 Upbit 차트 (KRW-ENS)</h2>
          </div>
          <div className="section-content">
            <RTUpbitChartENS />
          </div>
        </div>

        {/* 코인 백테스트 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>코인 백테스트</h2>
          </div>
          <div className="section-content">
            {isProduction ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 프로덕션 환경에서는 이용할 수 없습니다
                </p>
              </div>
            ) : (
              <BTCoinBacktest />
            )}
          </div>
        </div>

        {/* 코인 백테스트 (Mock) 섹션 */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>코인 백테스트 (Mock)</h2>
          </div>
          <div className="section-content">
            {isProduction ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 프로덕션 환경에서는 이용할 수 없습니다
                </p>
              </div>
            ) : (
              <BTCoinBacktestMock />
            )}
          </div>
        </div>




        {/* 두 번째 행: 시스템 상태 모니터링 Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>시스템 상태 모니터링</h2>
          </div>
          <div className="section-content">
            <Heartbeats />
          </div>
        </div>

        {/* 세 번째 행: 계좌 정보 Section */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>🏦 계좌 정보</h2>
            <p>키움증권 계좌 및 보유 종목 현황</p>
          </div>
          <div className="section-content">
            <div style={{
              width: '100%',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              padding: '16px',
              fontSize: '12px',
              color: '#e6edf3',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              textAlign: 'center'
            }}>
              <div style={{ color: '#fbbf24' }}>계좌 정보 기능이 제거되었습니다</div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default Dashboard;